import { Injectable } from '@angular/core';
import { DynamicsPessoaFisica } from 'src/app/data/Dynamics/pessoa-fisica/dynamics-pessoa-fisica.service';
import { LoggerService } from 'src/app/core/services/logger.service';
import { UtilService } from 'src/app/core/services/util.service';
import { TicketCreationRequestControlService } from 'src/app/shared/services/ticket-creation-request-control.service';
import { CIFDatadogService } from 'src/app/shared/services/cif-datadog';
import { environment } from 'src/environments/environment';
import { JsonConverterUtils } from 'src/app/data/CXOne/util/JsonConverterUtils';
import { LocalStorageUtils } from 'src/app/data/CXOne/util/LocalStorageUtils';

interface CIFFrameworkApi {
  setMode(mode: number): Promise<unknown> | void;
  addHandler(
    eventName: string,
    handler: (eventData: unknown) => Promise<void>,
  ): Promise<unknown> | void;
}

type RuntimeWindow = Window & {
  Microsoft?: {
    CIFramework?: CIFFrameworkApi;
  };
};

export const cifResizePocConfig = {
  enabled: true,

  initialModeEnabled: true,
  initialMode: 1,

  preventHidden: false,
  dockOnNewInteraction: true,

  allowMinimized: true,
  allowCollapsed: true,
};

@Injectable()
export class DynamicsPessoaFisicaResizePocService extends DynamicsPessoaFisica {
  constructor(
    private resizePocLogger: LoggerService,
    utilService: UtilService,
    ticketCreationRequestControlService: TicketCreationRequestControlService,
    cifDatadogService: CIFDatadogService,
    jsonConverterUtils: JsonConverterUtils,
    localStorageUtils: LocalStorageUtils,
  ) {
    super(
      resizePocLogger,
      utilService,
      ticketCreationRequestControlService,
      cifDatadogService,
      jsonConverterUtils,
      localStorageUtils,
    );
  }

  override initialize(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const cifUrl = environment.cif_versao2_pf;
      this.resizePocLogger.info(
        `${this.service} - inicializando CIF: ${cifUrl}`,
      );

      const script = this.createPocScriptElement(cifUrl);
      document.body.appendChild(script);

      const onCIFInitDone = async () => {
        try {
          await this.configureCIFResizePoc();
          this.resizePocLogger.info(
            `${this.service} - CIF inicializado com sucesso (PF RESIZE POC)`,
          );
          resolve();
        } catch (error) {
          this.resizePocLogger.error(
            `${this.service} - Erro ao criar sessão (PF RESIZE POC): `,
            error,
          );

          reject(
            new Error(
              `${this.service} - Erro ao criar sessão (PF RESIZE POC): ${error instanceof Error ? error.message : error}`,
            ),
          );
        } finally {
          window.removeEventListener('CIFInitDone', onCIFInitDone);
        }
      };

      window.addEventListener('CIFInitDone', onCIFInitDone);
    });
  }

  private createPocScriptElement(cifUrl: string): HTMLScriptElement {
    const script = document.createElement('script');
    script.src = cifUrl;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-crmurl', cifUrl);
    script.setAttribute('data-cifid', 'CIFMainLibrary');
    return script;
  }

  private async configureCIFResizePoc(): Promise<void> {
    if (!cifResizePocConfig.enabled) {
      this.resizePocLogger.info(
        `${this.service} - Configuração CIF da POC desabilitada.`,
      );
      return;
    }

    try {
      const ciFramework = this.getCIFramework();

      if (!ciFramework) {
        throw new Error('Microsoft.CIFramework nao esta disponivel.');
      }

      if (cifResizePocConfig.initialModeEnabled) {
        await ciFramework.setMode(cifResizePocConfig.initialMode);
      }

      await ciFramework.addHandler(
        'onmodechanged',
        async (eventData: unknown) => {
          try {
            const mode = this.getCIFModeFromEvent(eventData);
            this.resizePocLogger.info(
              `${this.service} - onmodechanged recebido. Modo atual: `,
              mode,
            );

            if (mode === undefined) {
              this.resizePocLogger.warn(
                `${this.service} - onmodechanged sem modo extraivel. EventData recebido: `,
                eventData,
              );
              return Promise.resolve();
            }

            // CIF modes:
            // 0: Minimized
            // 1: Docked
            // 2: Hidden
            // 3: Collapsed
            if (mode === 2 && cifResizePocConfig.preventHidden) {
              this.resizePocLogger.info(
                `${this.service} - Hidden detectado e bloqueado. Retornando para docked.`,
              );
              await ciFramework.setMode(1);
              return Promise.resolve();
            }

            if (mode === 0 && !cifResizePocConfig.allowMinimized) {
              this.resizePocLogger.info(
                `${this.service} - Minimized detectado e bloqueado. Retornando para docked.`,
              );
              await ciFramework.setMode(1);
              return Promise.resolve();
            }

            if (mode === 3 && !cifResizePocConfig.allowCollapsed) {
              this.resizePocLogger.info(
                `${this.service} - Collapsed detectado e bloqueado. Retornando para docked.`,
              );
              await ciFramework.setMode(1);
              return Promise.resolve();
            }
          } catch (error) {
            this.resizePocLogger.error(
              `${this.service} - Erro no handler onmodechanged da POC: `,
              error,
            );
          }

          return Promise.resolve();
        },
      );
    } catch (error) {
      this.resizePocLogger.error(
        `${this.service} - Erro ao configurar CIF (PF RESIZE POC): `,
        error,
      );

      throw new Error(
        `${this.service} - Erro ao configurar CIF (PF RESIZE POC): ${error instanceof Error ? error.message : error}`,
      );
    }
  }

  private getCIFModeFromEvent(eventData: unknown): number | undefined {
    if (eventData === undefined || eventData === null) {
      return undefined;
    }

    if (typeof eventData === 'number') {
      return eventData;
    }

    if (typeof eventData === 'string') {
      const directMode = this.normalizeModeValue(eventData.trim());

      if (directMode !== undefined) {
        return directMode;
      }

      try {
        const parsed = JSON.parse(eventData);

        if (typeof parsed === 'number' || typeof parsed === 'string') {
          return this.normalizeModeValue(parsed);
        }

        const valueFromPayload = this.extractModeValue(parsed);

        return this.normalizeModeValue(valueFromPayload);
      } catch {
        return undefined;
      }
    }

    const valueFromObject = this.extractModeValue(eventData);

    return this.normalizeModeValue(valueFromObject);
  }

  private extractModeValue(eventData: unknown): unknown {
    if (!this.isRecord(eventData)) {
      return undefined;
    }

    const nestedData = eventData['data'];
    const nestedMode = this.isRecord(nestedData)
      ? nestedData['mode']
      : undefined;

    return (
      eventData['mode'] ??
      eventData['Mode'] ??
      eventData['value'] ??
      eventData['Value'] ??
      nestedMode
    );
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private normalizeModeValue(modeValue: unknown): number | undefined {
    if (typeof modeValue === 'number' && Number.isFinite(modeValue)) {
      return modeValue;
    }

    if (typeof modeValue === 'string') {
      const parsedMode = Number(modeValue);

      if (Number.isFinite(parsedMode)) {
        return parsedMode;
      }
    }

    return undefined;
  }

  private async dockCIFOnNewInteraction(): Promise<void> {
    if (!cifResizePocConfig.enabled) {
      return;
    }

    if (!cifResizePocConfig.dockOnNewInteraction) {
      return;
    }

    try {
      const ciFramework = this.getCIFramework();

      if (!ciFramework) {
        this.resizePocLogger.info(
          `PF RESIZE POC - CIF nao disponivel para aplicar dock em nova interacao.`,
        );
        return;
      }

      this.resizePocLogger.info(
        `PF RESIZE POC - Nova interação recebida, trazendo CIF para docked`,
      );

      await ciFramework.setMode(1);
    } catch (error) {
      this.resizePocLogger.error(
        `PF RESIZE POC - Erro ao trazer CIF para docked em nova interação: `,
        error,
      );
    }
  }

  public async onNewInteractionCIFControl(): Promise<void> {
    await this.dockCIFOnNewInteraction();
  }

  public async minimizeCIF(): Promise<void> {
    await this.applyCIFMode(0, 'Minimized');
  }

  public async dockCIF(): Promise<void> {
    await this.applyCIFMode(1, 'Docked');
  }

  public async hideCIF(): Promise<void> {
    await this.applyCIFMode(2, 'Hidden');
  }

  public async collapseCIF(): Promise<void> {
    await this.applyCIFMode(3, 'Collapsed');
  }

  private async applyCIFMode(mode: number, modeName: string): Promise<void> {
    try {
      const ciFramework = this.getCIFramework();

      if (!ciFramework) {
        this.resizePocLogger.warn(
          `${this.service} - CIF nao disponivel para aplicar modo ${modeName} (${mode}).`,
        );
        return;
      }

      this.resizePocLogger.info(
        `${this.service} - Aplicando modo CIF ${modeName} (${mode}).`,
      );

      await ciFramework.setMode(mode);
    } catch (error) {
      this.resizePocLogger.error(
        `${this.service} - Erro ao aplicar modo CIF ${modeName} (${mode}): `,
        error,
      );
    }
  }

  private getCIFramework(): CIFFrameworkApi | undefined {
    const runtimeWindow = window as RuntimeWindow;
    return runtimeWindow.Microsoft?.CIFramework;
  }
}
